"use client";

import {
  createContext,
  useContext,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";

import { ContactFormDialog } from "@/components/contact-form-dialog";
import { Button } from "@/components/ui/button";

const PhotonContactContext = createContext<(() => void) | null>(null);

export function PhotonContactProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const openDialog = () => setOpen(true);

  return (
    <PhotonContactContext.Provider value={openDialog}>
      {children}
      <ContactFormDialog open={open} onOpenChange={setOpen} />
    </PhotonContactContext.Provider>
  );
}

function usePhotonContactDialog() {
  const openDialog = useContext(PhotonContactContext);

  if (!openDialog) {
    throw new Error(
      "Photon contact controls must be used inside PhotonContactProvider"
    );
  }

  return openDialog;
}

type PhotonContactButtonProps = Omit<
  ComponentProps<typeof Button>,
  "asChild" | "onClick"
>;

export function PhotonContactButton({
  children = "Contact us",
  type = "button",
  ...props
}: PhotonContactButtonProps) {
  const openDialog = usePhotonContactDialog();

  return (
    <Button type={type} onClick={openDialog} {...props}>
      {children}
    </Button>
  );
}

type PhotonContactTextLinkProps = Omit<ComponentProps<"button">, "type" | "onClick">;

export function PhotonContactTextLink({
  children = "Contact us",
  className,
  ...props
}: PhotonContactTextLinkProps) {
  const openDialog = usePhotonContactDialog();

  return (
    <button
      type="button"
      onClick={openDialog}
      className={className}
      {...props}
    >
      {children}
    </button>
  );
}
